/**
 * Client calls for the three record-keeping surfaces added with attachments, the print screen and the alarm log
 * (URS-EXE-013, BL-026, BL-025). The JSON calls ride the shared `api` helper; the file upload and the file
 * downloads go through `authedFetch` because they carry bytes, not JSON.
 */
import { api, authedFetch } from "./api.js";

// ------------------------------------------------------------------ attachments (attachments.controller.ts)

export interface Attachment {
  id: string;
  batch_id: string;
  step_execution_id: string | null;
  data_point_id: string | null;
  filename: string;
  mime_type: string;
  byte_size: string;
  sha256: string;
  description: string | null;
  capture_device: string;
  scanner: string;
  captured_by_name: string;
  captured_at_server: string;
}

export interface AttachmentUpload {
  file: File;
  stepExecutionId?: string;
  dataPointId?: string;
  description?: string;
  device?: string;
}

/** The file types the server accepts, proven by content there; this only narrows the picker (SEC-036). */
export const ATTACHMENT_ACCEPT = "application/pdf,image/png,image/jpeg";

export async function uploadAttachment(batchId: string, u: AttachmentUpload): Promise<Attachment> {
  const form = new FormData();
  if (u.stepExecutionId) form.set("stepExecutionId", u.stepExecutionId);
  if (u.dataPointId) form.set("dataPointId", u.dataPointId);
  if (u.description?.trim()) form.set("description", u.description.trim());
  form.set("device", u.device ?? "web browser");
  form.set("file", u.file, u.file.name); // the file part last, after the fields that describe it
  const res = await authedFetch(`/api/v1/batches/${batchId}/attachments`, { method: "POST", body: form });
  return (await res.json()) as Attachment;
}

/** Hands a fetched file to the browser as a download; a bearer token cannot ride an <a href>. */
export async function downloadFile(
  path: string,
  fallbackName: string,
  init: RequestInit = {},
): Promise<void> {
  const res = await authedFetch(path, init);
  const named = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1];
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = named ?? fallbackName;
  a.click();
  URL.revokeObjectURL(url);
}

export const attachments = {
  list: (batchId: string, stepExecutionId?: string) =>
    api.get<Attachment[]>(
      `/api/v1/batches/${batchId}/attachments${stepExecutionId ? `?stepExecutionId=${stepExecutionId}` : ""}`,
    ),
  download: (a: Attachment) =>
    downloadFile(`/api/v1/batches/${a.batch_id}/attachments/${a.id}/file`, a.filename),
};

/** "12.4 KB" — SI units, never rounded past one decimal. */
export function formatBytes(raw: string | number): string {
  const n = Number(raw);
  if (n < 1000) return `${n} B`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)} KB`;
  return `${(n / 1_000_000).toFixed(1)} MB`;
}

// ------------------------------------------------------------------ print / render (archive.controller.ts)

export type CopyStatus = "TRUE_COPY" | "UNCONTROLLED_COPY";
export const REASON_CODES = [
  "INSPECTION",
  "INTERNAL_REVIEW",
  "CUSTOMER_REQUEST",
  "INVESTIGATION",
  "OTHER",
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];

export interface RenderRow {
  id: string;
  document_id: string;
  copy_status: CopyStatus;
  batch_status: string;
  include_audit_trail: boolean;
  reason_code: ReasonCode;
  reason_text: string | null;
  reference: string | null;
  source_hash: string;
  render_digest: string;
  file_sha256: string;
  byte_size: string;
  page_count: number;
  renderer: string;
  print_time_in_document: string;
  printed_at_server: string;
  printed_by_name: string;
}

export interface RenderResult {
  renderId: string;
  documentId: string;
  copyStatus: CopyStatus;
  pageCount: number;
  completeness: { present: number; expected: number; machineOnly: number };
  fileSha256: string;
  renderDigest: string;
  sourceHash: string;
  renderer: string;
  printedAt: string;
}

export interface RenderRequest {
  reasonCode: ReasonCode;
  reasonText?: string;
  reference?: string;
  includeAuditTrail: boolean;
}

export interface RenderVerification {
  roundTrip: boolean;
  embeddedHash: string | null;
}

/** The server decides TRUE COPY from the batch state and the audit trail; this is the same rule, to explain it before the click. */
export const FINAL_BATCH_STATES: ReadonlySet<string> = new Set(["QA_APPROVED", "CLOSED"]);

export const renders = {
  list: (batchId: string) => api.get<RenderRow[]>(`/api/v1/batches/${batchId}/renders`),
  create: (batchId: string, body: RenderRequest) =>
    api.post<RenderResult>(`/api/v1/batches/${batchId}/renders`, body),
  verify: (batchId: string, renderId: string) =>
    api.post<RenderVerification>(`/api/v1/batches/${batchId}/renders/${renderId}/verify`, {}),
  download: (batchId: string, r: { id: string; document_id: string }) =>
    downloadFile(`/api/v1/batches/${batchId}/renders/${r.id}/file`, `${r.document_id}.pdf`),
};

// ------------------------------------------------------------------ alarm log (alarm.controller.ts)

export type AlarmSeverity = "MINOR" | "MAJOR" | "CRITICAL";
export type AlarmSort = "at_server" | "severity" | "code";

export interface AlarmRow {
  id: string;
  code: string;
  severity: AlarmSeverity;
  source: string;
  message: string;
  correlation_id: string | null;
  occurrences_in_window: number;
  at_server: string;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  ack_comment: string | null;
  acknowledged_at: string | null;
}

export interface AlarmPage {
  total: number;
  rows: AlarmRow[];
}

export interface AlarmFilters {
  severity: "" | AlarmSeverity;
  acknowledged: "" | "true" | "false";
  code: string;
  source: string;
  q: string;
  from: string;
  to: string;
  sort: AlarmSort;
  dir: "asc" | "desc";
}

export const ALARM_PAGE_SIZE = 25;
export const ALARM_COMMENT_MIN = 10;
export const EMPTY_ALARM_FILTERS: AlarmFilters = {
  severity: "",
  acknowledged: "",
  code: "",
  source: "",
  q: "",
  from: "",
  to: "",
  sort: "at_server",
  dir: "desc",
};

/** Blank filters are left out; the date-time inputs are local time and travel as the instants they denote. */
export function alarmQuery(f: AlarmFilters, offset: number, limit = ALARM_PAGE_SIZE): string {
  const p = new URLSearchParams();
  for (const k of ["severity", "acknowledged", "code", "source", "q"] as const) {
    const v = f[k].trim();
    if (v) p.set(k, v);
  }
  for (const k of ["from", "to"] as const) {
    const t = Date.parse(f[k]);
    if (f[k] && !Number.isNaN(t)) p.set(k, new Date(t).toISOString());
  }
  p.set("sort", f.sort);
  p.set("dir", f.dir);
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  return p.toString();
}

export const alarms = {
  search: (query: string) => api.get<AlarmPage>(`/api/v1/alarms?${query}`),
  acknowledge: (id: string, comment: string) =>
    api.post<AlarmRow>(`/api/v1/alarms/${id}/acknowledge`, { comment }),
  /** The shell indicator: how many alarms nobody has acknowledged, and how many of those are CRITICAL. */
  unacknowledged: async () => {
    const [all, critical] = await Promise.all([
      api.get<AlarmPage>("/api/v1/alarms?acknowledged=false&limit=1"),
      api.get<AlarmPage>("/api/v1/alarms?acknowledged=false&severity=CRITICAL&limit=1"),
    ]);
    return { total: all.total, critical: critical.total };
  },
};
